import PropTypes from 'prop-types';

import React, { useState } from 'react';
import {
	ButtonDropdown,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from 'reactstrap';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import kebabImage from 'svgs/kebab';

/**
 * <IcalSubscribeMenu />
 *
 * Kebab (vertical 3-dot) menu surfacing iCal subscribe actions for the patient
 * Schedule tab. Mounts into the `headerAction` slot of `MonthCalendar`.
 *
 * Actions:
 *   - Copy Calendar Link             (full-detail feed)
 *   - Copy Privatized Calendar Link  (timing/type only)
 *   - Open in Calendar               (webcal:// hand-off)
 *   - Open Privatized in Calendar    (webcal:// hand-off, privatized)
 *   - Reset Link                     (revokes both URLs; parent handles regen)
 *
 * Props:
 *   - feedUrl:           string  (private feed URL, https://)
 *   - feedUrlPrivatized: string  (privatized feed URL, https://)
 *   - onReset:           () => void  (parent-supplied callback; this component
 *                                     does not call any API directly)
 */

const Kebab = styled.div`
	cursor: pointer;
	path {
		fill: ${(props) => (props.isActive ? 'var(--main-color)' : 'black')};
	}
`;

const ResetItem = styled(DropdownItem)`
	&&& {
		color: #dc3545;
	}
`;

const toWebcal = (url) =>
	typeof url === 'string' ? url.replace(/^https?:/, 'webcal:') : '';

const copyToClipboard = (text, successMessage) => {
	if (!text) return;
	if (navigator.clipboard && navigator.clipboard.writeText) {
		// `writeText` can reject (e.g., document not focused, permission denied
		// on insecure contexts). Only show success after the write resolves;
		// surface a fall-back error toast if it doesn't.
		Promise.resolve(navigator.clipboard.writeText(text))
			.then(() =>
				toast.success(successMessage, {
					position: toast.POSITION.TOP_CENTER,
					autoClose: 2000,
				}),
			)
			.catch(() => {
				if (toast.error) {
					toast.error('Could not copy to clipboard', {
						position: toast.POSITION.TOP_CENTER,
						autoClose: 2000,
					});
				}
			});
	}
};

const openInCalendar = (url) => {
	if (!url) return;
	// `noopener,noreferrer` isolates the new context from this window even
	// though `webcal://` is usually handed off to a native calendar app.
	window.open(toWebcal(url), '_blank', 'noopener,noreferrer');
};

const IcalSubscribeMenu = (props) => {
	const { feedUrl, feedUrlPrivatized, onReset } = props;
	const [isOpen, setIsOpen] = useState(false);

	const toggle = () => setIsOpen((prev) => !prev);

	const hasPrivate = Boolean(feedUrl);
	const hasPrivatized = Boolean(feedUrlPrivatized);
	const clipboardAvailable = Boolean(
		typeof navigator !== 'undefined' &&
			navigator.clipboard &&
			navigator.clipboard.writeText,
	);

	const handleCopyPrivate = () =>
		copyToClipboard(feedUrl, 'Calendar link copied');
	const handleCopyPrivatized = () =>
		copyToClipboard(feedUrlPrivatized, 'Privatized calendar link copied');
	const handleOpenPrivate = () => openInCalendar(feedUrl);
	const handleOpenPrivatized = () => openInCalendar(feedUrlPrivatized);
	const handleReset = () => {
		if (onReset) onReset();
	};

	return (
		<ButtonDropdown
			isOpen={isOpen}
			toggle={toggle}
			direction="down"
			className="ical-subscribe-menu"
		>
			<DropdownToggle
				tag="button"
				type="button"
				aria-label="Calendar subscription menu"
				className="btn btn-link p-2"
				style={{
					background: 'transparent',
					border: 'none',
					boxShadow: 'none',
				}}
			>
				<Kebab isActive={isOpen}>{kebabImage}</Kebab>
			</DropdownToggle>
			<DropdownMenu right flip={false}>
				<DropdownItem
					onClick={handleCopyPrivate}
					disabled={!hasPrivate || !clipboardAvailable}
				>
					Copy Calendar Link
				</DropdownItem>
				<DropdownItem
					onClick={handleCopyPrivatized}
					disabled={!hasPrivatized || !clipboardAvailable}
				>
					Copy Privatized Calendar Link
				</DropdownItem>
				<DropdownItem divider />
				<DropdownItem
					onClick={handleOpenPrivate}
					disabled={!hasPrivate}
				>
					Open in Calendar
				</DropdownItem>
				<DropdownItem
					onClick={handleOpenPrivatized}
					disabled={!hasPrivatized}
				>
					Open Privatized in Calendar
				</DropdownItem>
				<DropdownItem divider />
				<ResetItem onClick={handleReset}>Reset Link</ResetItem>
			</DropdownMenu>
		</ButtonDropdown>
	);
};

IcalSubscribeMenu.propTypes = {
	feedUrl: PropTypes.string,
	feedUrlPrivatized: PropTypes.string,
	onReset: PropTypes.func,
};

IcalSubscribeMenu.defaultProps = {
	feedUrl: '',
	feedUrlPrivatized: '',
	onReset: undefined,
};

export default IcalSubscribeMenu;
